import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Sparkles, ArrowRight, Brain, Users, Handshake, Star, Zap,
  GraduationCap, IdCard, Rocket, Share2, Trophy, Search, Award, Globe,
  Calendar, ChevronRight, ChevronDown, Briefcase, BookOpen, FileText,
  User, ExternalLink, Bell, Flame
} from "lucide-react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { useVoiceWidget } from "@/context/VoiceWidgetContext";

// Course Categories with YouTube Links
const COURSE_CATEGORIES = [
  { id: 1, name: "Business Operations", icon: "💼", url: "https://www.youtube.com/playlist?list=PL5_qO7P2XjBd86Pw0SQVTaA88nHTj1I3e" },
  { id: 2, name: "Cloud Computing", icon: "☁️", url: "https://www.youtube.com/watch?v=2LaAJq1lB1Q" },
  { id: 3, name: "Communication", icon: "💬", url: "https://www.youtube.com/watch?v=A0BMyN3Eofk" },
  { id: 4, name: "Continuing Education Units", icon: "🎓", url: "https://www.youtube.com/watch?v=ABh6yk11YUM" },
  { id: 5, name: "Cybersecurity", icon: "🔒", url: "https://www.youtube.com/watch?v=9aB3blo_Ctc" },
  { id: 6, name: "Data Science", icon: "📊", url: "https://www.youtube.com/watch?v=ub1BX0I2GCs" },
  { id: 7, name: "Design Tools", icon: "🎨", url: "https://www.youtube.com/watch?v=s4nbdYhpdLQ" },
  { id: 8, name: "Development", icon: "💻", url: "https://www.youtube.com/watch?v=pmCgTGvU53g" },
  { id: 9, name: "Finance & Accounting", icon: "💰", url: "https://www.youtube.com/watch?v=v-djL7SPw4c" },
  { id: 10, name: "Generative AI", icon: "🤖", url: "https://www.youtube.com/watch?v=ZLFdKymeqgw" },
  { id: 11, name: "IT Operations", icon: "⚙️", url: "https://www.youtube.com/watch?v=5KtRF4NuUWE" },
  { id: 12, name: "Language Learning", icon: "🌍", url: "https://www.youtube.com/watch?v=QkXfWALFQ68" },
  { id: 13, name: "Leadership & Management", icon: "👔", url: "https://www.youtube.com/watch?v=FROcYSRS0yU" },
  { id: 14, name: "Marketing", icon: "📢", url: "https://www.youtube.com/playlist?list=PL9ooVrP1hQOH-nMMFeSG-DFwyATtUg4YA" },
  { id: 15, name: "Personal Development", icon: "🌱", url: "https://www.youtube.com/watch?v=B3sxFFGi2Rk" },
  { id: 16, name: "Productivity", icon: "⚡", url: "https://www.youtube.com/playlist?list=PL4FmbPBGaOC5kb9x2lbepYWYCCAno1AZI" },
  { id: 17, name: "Project & Product Management", icon: "📋", url: "https://www.youtube.com/watch?v=9V1tpRaEBaw" },
  { id: 18, name: "Sales & Customer Service", icon: "🤝", url: "https://www.youtube.com/playlist?list=PL4n1q6xm1u-I17fql7jKKbAe4Ior9fZBY" },
  { id: 19, name: "Workplace & Human Resources", icon: "👥", url: "https://www.youtube.com/playlist?list=PL3Y_p3e-Lne31qsip5QPMjsEEYd0fqpKy" },
  { id: 20, name: "UX, Web & Mobile Design", icon: "📱", url: "https://www.youtube.com/watch?v=WylVZzSsavg" }
];

// Quick Links
const QUICK_LINKS = [
  { id: 1, name: "JunicornFinder", icon: Search, color: "violet", url: "https://dev.junicornshub.com", external: true },
  { id: 2, name: "TalentON Hackathon", icon: Trophy, color: "amber", url: "https://explore.talenton.ai/", external: true },
  { id: 3, name: "Talent Pipeline Program", icon: GraduationCap, color: "emerald", url: "/karnataka-model", external: false },
  { id: 4, name: "Gemma Offline AI", icon: Globe, color: "green", url: "/gemma", external: false },
  { id: 5, name: "Content Command Centre", icon: Sparkles, color: "fuchsia", url: "/content", external: false },
  { id: 6, name: "Captain Command Centre", icon: Award, color: "blue", url: "/command-centre", external: false },
  { id: 7, name: "WhatsApp NDA Sign", icon: Share2, color: "green", url: "/whatsapp", external: false }
];

// 5 Entry Points
const ENTRY_POINTS = [
  { id: "pupil", title: "PUPIL", icon: GraduationCap, color: "from-blue-500 to-indigo-600", path: "/role-play" },
  { id: "people", title: "PEOPLE", icon: Users, color: "from-green-500 to-emerald-600", path: "/auth?role=doer" },
  { id: "profiles", title: "PROFILES", icon: IdCard, color: "from-pink-500 to-rose-600", path: "/dp" },
  { id: "partners", title: "PARTNERS", icon: Handshake, color: "from-purple-500 to-violet-600", path: "/auth?role=employer" },
  { id: "performers", title: "PERFORMERS", icon: Star, color: "from-amber-500 to-orange-600", path: "/auth?role=doer&type=performer" }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openWidget } = useVoiceWidget();
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchJobs();
  }, [user]);

  const fetchJobs = async () => {
    try {
      const jobsRes = await axios.get(`${API}/jobs`, { params: { division: user?.division, limit: 5 } });
      setJobs(jobsRes.data);
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLinkClick = (link) => {
    if (link.external) {
      window.open(link.url, '_blank');
    } else {
      navigate(link.url);
    }
    setActiveDropdown(null);
  };

  const doersId = `RDW-${user?.pincode || '000000'}-${(user?.division || 'GEN').slice(0, 3).toUpperCase()}-L1`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 overflow-x-hidden">
      {/* Cosmic background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
      </div>

      {/* VAPI-Style Header - Single Line Navbar */}
      <header className="fixed top-0 left-0 right-0 z-[9999] bg-slate-950/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center">

          {/* Left - Logo (fixed width for symmetry) */}
          <div className="flex-shrink-0 w-40 flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-sm leading-tight">HI AI</h1>
              <p className="text-white/40 text-[9px]">APP.COM</p>
            </div>
          </div>

          {/* Center - Navigation Links (flex-1 to center) */}
          <nav className="hidden md:flex flex-1 items-center justify-center gap-8">
            {/* Quick Links */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('quicklinks')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-medium tracking-wider uppercase transition-colors">
                SERVICES
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {activeDropdown === 'quicklinks' && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-slate-900 rounded-xl shadow-2xl border border-white/10 min-w-[220px] overflow-hidden"
                  >
                    {QUICK_LINKS.map((link) => {
                      const Icon = link.icon;
                      return (
                        <button
                          key={link.id}
                          onClick={() => handleQuickLinkClick(link)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-500/10 text-left transition-colors group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center group-hover:from-purple-500/30 group-hover:to-pink-500/30 transition-colors">
                            <Icon className="w-4 h-4 text-purple-400" />
                          </div>
                          <span className="text-white/90 text-sm font-medium">{link.name}</span>
                          {link.external && <ExternalLink className="w-3.5 h-3.5 text-white/30 ml-auto" />}
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* My Reports */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('reports')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-medium tracking-wider uppercase transition-colors">
                SUITABILITY REPORT
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {activeDropdown === 'reports' && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-slate-900 rounded-xl shadow-2xl border border-white/10 min-w-[220px] p-4"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">My Reports</p>
                        <p className="text-white/50 text-xs">No reports yet</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        navigate('/aimee-analyzer');
                        setActiveDropdown(null);
                      }}
                      className="w-full py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white rounded-lg text-sm font-semibold transition-all shadow-lg shadow-purple-500/20"
                    >
                      Take Assessment
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Courses */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('courses')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-medium tracking-wider uppercase transition-colors">
                TALENTON PROGRAM
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {activeDropdown === 'courses' && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-slate-900 rounded-xl shadow-2xl border border-white/10 w-[340px] max-h-[400px] overflow-y-auto"
                  >
                    <div className="grid grid-cols-2 gap-1 p-3">
                      {COURSE_CATEGORIES.map((category) => (
                        <a
                          key={category.id}
                          href={category.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setActiveDropdown(null)}
                          className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-purple-500/10 rounded-lg text-left transition-colors group"
                        >
                          <span className="text-lg">{category.icon}</span>
                          <span className="text-white/80 text-xs font-medium group-hover:text-white transition-colors">{category.name}</span>
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Jobs */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('jobs')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-medium tracking-wider uppercase transition-colors">
                JOBS4ME
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {activeDropdown === 'jobs' && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-slate-900 rounded-xl shadow-2xl border border-white/10 min-w-[280px] max-h-[400px] overflow-y-auto"
                  >
                    {loading ? (
                      <div className="px-4 py-3">
                        <p className="text-white/50 text-sm">Loading jobs...</p>
                      </div>
                    ) : jobs.length > 0 ? (
                      <>
                        {jobs.map((job) => (
                          <button
                            key={job.id}
                            onClick={() => {
                              navigate(`/jobs?selected=${job.id}`);
                              setActiveDropdown(null);
                            }}
                            className="w-full flex items-start gap-3 px-4 py-3 hover:bg-purple-500/10 text-left transition-colors border-b border-white/5 last:border-b-0 group"
                          >
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <Briefcase className="w-4 h-4 text-purple-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white/90 text-sm font-medium truncate group-hover:text-white transition-colors">{job.title}</p>
                              <p className="text-white/50 text-xs truncate">{job.company_name}</p>
                            </div>
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            navigate('/jobs');
                            setActiveDropdown(null);
                          }}
                          className="w-full px-4 py-3 text-purple-400 hover:bg-purple-500/10 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          View All Jobs
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <div className="px-4 py-3">
                        <p className="text-white/50 text-sm">No jobs available</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Profile Link */}
            <button
              onClick={() => navigate('/dp')}
              className="text-white/70 hover:text-white text-xs font-medium tracking-wider uppercase transition-colors"
            >
              DOERS PROFILE
            </button>
          </nav>

          {/* Right - CTA Button (fixed width for symmetry) */}
          <div className="flex-shrink-0 w-40 flex justify-end">
            <a
              href="https://calendly.com/doersworld-contact"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg shadow-black/30 hover:shadow-xl hover:shadow-purple-500/20 hover:scale-105 transition-all duration-300 border border-gray-500"
            >
              <Calendar className="w-3.5 h-3.5 group-hover:animate-pulse" />
              Book a Call
            </a>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16" />

      <main className="relative z-10 px-4 pb-24 max-w-lg mx-auto">

        {/* Hero Section */}
        <section className="text-center py-6">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Badge className="mb-3 bg-orange-500/20 text-orange-300 border-orange-500/30">
              🌍 WORLD EXPO 2031 BOUND
            </Badge>
          </motion.div>

          <motion.h1
            className="text-4xl md:text-5xl font-bold text-white mb-2"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400">Doers World</span>
          </motion.h1>

          <motion.p
            className="text-white/60 text-sm mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            An AGENTIC AI for Dreamers • Doers • Disrupters • Deep Humans
          </motion.p>
        </section>

        {/* JOIN THE WAVE + Robot Cards */}
        <section className="mb-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Card 1 - Join The Wave */}
            <motion.div
              whileHover={{ scale: 1.03, y: -4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/dp')}
              className="cursor-pointer group"
            >
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-600/5 border border-amber-500/20 p-6 h-full backdrop-blur-sm transition-all duration-300 group-hover:border-amber-400/40 group-hover:shadow-lg group-hover:shadow-amber-500/10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-amber-500/30 transition-colors" />
                <div className="relative mb-4">
                  <span className="text-4xl">🌊</span>
                </div>
                <div className="relative">
                  <h3 className="text-amber-300 font-bold text-lg mb-1">Join The Wave</h3>
                  <p className="text-white/50 text-sm mb-4">The Gold Rush is HERE. Share your Doers Profile & get discovered.</p>
                  <div className="flex items-center gap-2 text-amber-400 text-sm font-medium group-hover:gap-3 transition-all">
                    <span>Get Talent Card</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Card 2 - Robot Placeholder */}
            <motion.div
              whileHover={{ scale: 1.03, y: -4 }}
              whileTap={{ scale: 0.98 }}
              className="cursor-pointer group"
            >
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-600/5 border border-cyan-500/20 p-6 h-full backdrop-blur-sm transition-all duration-300 group-hover:border-cyan-400/40 group-hover:shadow-lg group-hover:shadow-cyan-500/10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-cyan-500/30 transition-colors" />
                <div className="relative mb-4">
                  <motion.span
                    className="text-4xl inline-block"
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    🤖
                  </motion.span>
                </div>
                <div className="relative">
                  <h3 className="text-cyan-300 font-bold text-lg mb-1">Coming Soon</h3>
                  <p className="text-white/50 text-sm mb-4">Something exciting is brewing. Stay tuned for updates!</p>
                  <div className="flex items-center gap-2 text-cyan-400 text-sm font-medium group-hover:gap-3 transition-all">
                    <span>Learn More</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* HERO CTA - Career Assessment */}
        <section className="mb-12 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-8"
          >
            {/* CTA Button */}
            <div className="flex flex-col items-center justify-center gap-4">
              <Button
                className="h-14 px-8 text-lg font-bold bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 shadow-lg shadow-purple-500/30 rounded-full w-full sm:w-80 flex items-center justify-center"
                onClick={openWidget}
                data-testid="voice-assessment-cta"
              >
                <div className="w-5 h-5 mr-3 flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.6)]" />
                </div>
                Take Voice Assessment
              </Button>

              <Button
                className="h-14 px-8 text-lg font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 hover:opacity-90 shadow-lg shadow-indigo-500/30 rounded-full w-full sm:w-80 flex items-center justify-center"
                onClick={() => navigate('/aimee-analyzer')}
              >
                <Sparkles className="w-5 h-5 mr-3" />
                Start Career Assessment
              </Button>
            </div>

            {/* Time indicator */}
            <p className="text-white/40 text-sm">
              ⏱️ Takes only 5-10 minutes • Powered by AI
            </p>
          </motion.div>
        </section>



        {/* WILL AI? - Modern Redesign */}
        <section className="mb-12">
          {/* Section Header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Will <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">AI</span> ...?
            </h2>
            <p className="text-white/50 text-sm">Choose your path and let AIMEE guide you</p>
          </div>

          {/* Two Column Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Card 1 - Job Concern */}
            <motion.div
              whileHover={{ scale: 1.03, y: -4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/aimee-analyzer?concern=jobs")}
              className="cursor-pointer group"
              data-testid="question-jobs"
            >
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-500/10 to-red-600/5 border border-rose-500/20 p-6 h-full backdrop-blur-sm transition-all duration-300 group-hover:border-rose-400/40 group-hover:shadow-lg group-hover:shadow-rose-500/10">
                {/* Glow effect */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-rose-500/30 transition-colors" />

                {/* Icon */}
                <div className="relative mb-4">
                  <span className="text-4xl">😰</span>
                </div>

                {/* Content */}
                <div className="relative">
                  <h3 className="text-rose-300 font-bold text-lg mb-1">Take away my job?</h3>
                  <p className="text-white/50 text-sm mb-4">I need guidance on navigating the AI revolution</p>

                  {/* CTA */}
                  <div className="flex items-center gap-2 text-rose-400 text-sm font-medium group-hover:gap-3 transition-all">
                    <span>Get Career Insights</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Card 2 - Growth */}
            <motion.div
              whileHover={{ scale: 1.03, y: -4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/aimee-analyzer?concern=growth")}
              className="cursor-pointer group"
              data-testid="question-life"
            >
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-600/5 border border-emerald-500/20 p-6 h-full backdrop-blur-sm transition-all duration-300 group-hover:border-emerald-400/40 group-hover:shadow-lg group-hover:shadow-emerald-500/10">
                {/* Glow effect */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-emerald-500/30 transition-colors" />

                {/* Icon */}
                <div className="relative mb-4">
                  <span className="text-4xl">🚀</span>
                </div>

                {/* Content */}
                <div className="relative">
                  <h3 className="text-emerald-300 font-bold text-lg mb-1">Make my life better?</h3>
                  <p className="text-white/50 text-sm mb-4">I want to grow and unlock my full potential</p>

                  {/* CTA */}
                  <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium group-hover:gap-3 transition-all">
                    <span>Start Growing</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* COSMIC FLYWHEEL */}
        <section className="mb-8">
          <h2 className="text-white text-xl font-bold text-center mb-4">Who Are You?</h2>

          <div className="relative w-72 h-72 mx-auto">
            {/* Glow */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-orange-500/20 blur-2xl" />

            {/* Rotating entries */}
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: 360 }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            >
              {ENTRY_POINTS.map((entry, idx) => {
                const angle = (idx * 72) - 90;
                const x = 50 + 40 * Math.cos((angle * Math.PI) / 180);
                const y = 50 + 40 * Math.sin((angle * Math.PI) / 180);

                return (
                  <motion.div
                    key={entry.id}
                    className="absolute cursor-pointer"
                    style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}
                    whileHover={{ scale: 1.2 }}
                    onClick={() => navigate(entry.path)}
                    data-testid={`flywheel-${entry.id}`}
                  >
                    <motion.div animate={{ rotate: -360 }} transition={{ duration: 30, repeat: Infinity, ease: "linear" }}>
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${entry.color} flex flex-col items-center justify-center shadow-lg border border-white/20`}>
                        <entry.icon className="w-5 h-5 text-white" />
                        <span className="text-white text-[8px] font-bold">{entry.title}</span>
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* CENTER - AIMEE */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.button
                className="w-28 h-28 rounded-full bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 shadow-2xl border-2 border-white/30 flex flex-col items-center justify-center"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                animate={{ boxShadow: ["0 0 20px rgba(139,92,246,0.3)", "0 0 40px rgba(139,92,246,0.5)", "0 0 20px rgba(139,92,246,0.3)"] }}
                transition={{ duration: 2, repeat: Infinity }}
                onClick={() => navigate('/aimee-analyzer')}
                data-testid="flywheel-center-aimee"
              >
                <Brain className="w-8 h-8 text-white mb-1" />
                <span className="text-white font-bold text-sm">AIMEE</span>
                <span className="text-white/70 text-[9px]">Start Here</span>
              </motion.button>
            </div>
          </div>
        </section>



        {/* Footer Tagline */}
        <section className="text-center">
          <p className="text-white/30 text-xs">
            🌍 Find Right Doers • Human Xperts • AI Xpert Agents • Robo Helpers
          </p>
          <p className="text-white/20 text-[10px] mt-1">
            HI AI-APP.COM • Way of Work • Soonicorn 2026
          </p>
        </section>
      </main>

    </div>
  );
}
